import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Calendar } from "lucide-react";
import Nav from "@/components/Nav";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    axios
      .get(`${API}/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  return (
    <div data-testid="blog-post-page" className="min-h-screen bg-[var(--bg)] text-[var(--text-1)]">
      {post && (
        <Seo
          title={`${post.title} · KS DevArc`}
          description={post.excerpt || post.content.slice(0, 160)}
          image={post.cover_image}
          url={typeof window !== "undefined" ? window.location.href : ""}
        />
      )}
      <Nav />
      <article className="relative pt-36 pb-32">
        <div className="absolute inset-0 grid-bg opacity-15 pointer-events-none" />
        <div className="relative mx-auto max-w-3xl px-5 sm:px-8">
          <Link
            to="/blog"
            data-testid="blog-back-link"
            className="inline-flex items-center gap-2 font-mono-ui text-[11px] tracking-[0.22em] uppercase text-[var(--text-3)] hover:text-[var(--text-1)] mb-10"
          >
            <ArrowLeft className="w-3 h-3" /> All posts
          </Link>

          {notFound && (
            <div data-testid="blog-not-found" className="border border-[var(--border-dim)] rounded-sm p-10 text-center">
              <div className="font-display text-2xl">Post not found</div>
              <div className="overline mt-3">It may have been moved or unpublished.</div>
            </div>
          )}

          {post && (
            <>
              {post.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {post.tags.map((t) => (
                    <span key={t} className="chip">{t}</span>
                  ))}
                </div>
              )}

              <h1
                data-testid="blog-post-title"
                className="font-display font-black text-4xl md:text-6xl tracking-tight leading-[0.95]"
              >
                {post.title}
              </h1>

              <div className="mt-6 flex items-center gap-3 font-mono-ui text-[11px] tracking-[0.22em] uppercase text-[var(--text-3)]">
                <Calendar className="w-3 h-3" />
                {new Date(post.created_at).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "long",
                  day: "2-digit",
                })}
              </div>

              {post.cover_image && (
                <img
                  src={post.cover_image}
                  alt={post.title}
                  className="mt-10 w-full rounded-sm border border-[var(--border-dim)]"
                />
              )}

              <div
                data-testid="blog-post-content"
                className="prose prose-invert max-w-none mt-12 prose-headings:font-display prose-headings:tracking-tight prose-a:text-[var(--vermilion)] prose-strong:text-[var(--text-1)]"
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
              </div>
            </>
          )}
        </div>
      </article>
    </div>
  );
}
